import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Search, CalendarRange, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Resource, Booking, Project } from '@/types';
import {
  RecommendationEngineOutput,
  RecommendationFilterState,
  ResourceMatchResult,
} from '@/types/recommendation';
import { computeRecommendations } from '@/lib/recommendationEngine';
import { useSignalWeights } from '@/hooks/useSignalWeights';
import { MultiResourceSelect } from './MultiResourceSelect';
import { ResourceSearchModal } from './ResourceSearchModal';
import { RecommendationSearchBar } from './RecommendationSearchBar';
import { RecommendedResourceList } from './RecommendedResourceList';
import { ConflictResourceList } from './ConflictResourceList';
import { RecommendationLoadingState } from './RecommendationLoadingState';
import { EmptyRecommendationState } from './EmptyRecommendationState';

interface ResourceRecommendationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  startDate: Date | undefined;
  endDate: Date | undefined;
  requiredRole?: string;
  location?: string;
  requiredSkills?: string[];
  projectId?: string | null;
  projects?: Project[];
  resources: Resource[];
  existingBookings: Booking[];
  currentResourceIds: string[];
  onConfirm: (
    resourceIds: string[],
    conflictOverrides: { conflictingBookingId: string; resourceId: string }[]
  ) => void;
}

const DEFAULT_FILTER: RecommendationFilterState = {
  searchQuery: '',
  roleFilter: null,
  locationFilter: null,
  minMatchScore: 0,
  departmentFilter: null,
};

export function ResourceRecommendationDialog({
  open,
  onOpenChange,
  startDate,
  endDate,
  requiredRole,
  location,
  requiredSkills = [],
  projectId,
  projects = [],
  resources,
  existingBookings,
  currentResourceIds,
  onConfirm,
}: ResourceRecommendationDialogProps) {
  const [recommendationOutput, setRecommendationOutput] =
    useState<RecommendationEngineOutput | null>(null);
  // Start as false — engine effect will set true then false, preventing initial flash
  const [isLoading, setIsLoading] = useState(false);
  const [pendingResourceIds, setPendingResourceIds] = useState<string[]>([]);
  const [filter, setFilter] = useState<RecommendationFilterState>(DEFAULT_FILTER);
  const [activeTab, setActiveTab] = useState<'suggested' | 'manual'>('suggested');


  const [resourceSearchOpen, setResourceSearchOpen] = useState(false);
  // Soft-gate: true when the planner clicks Confirm while unacknowledged conflicts
  // exist — shows an inline warning prompt before committing.
  const [overrideConfirmPending, setOverrideConfirmPending] = useState(false);
  const signalWeights = useSignalWeights();

  // Track whether the user has explicitly cleared the default role filter
  const [userOverriddenDefaultRole, setUserOverriddenDefaultRole] = useState(false);

  // Track previous open value to only reset when transitioning false → true
  const prevOpenRef = useRef(false);

  // Reset state only when dialog transitions from closed → open
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    if (open && !wasOpen) {
      setPendingResourceIds(currentResourceIds);


      setActiveTab('suggested');
      setUserOverriddenDefaultRole(false);
      setOverrideConfirmPending(false);
      setRecommendationOutput(null);
      setFilter({
        ...DEFAULT_FILTER,
        roleFilter: requiredRole ?? null,
      });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Engine input memoised to prevent unnecessary recalculations
  const engineInput = useMemo(
    () => ({
      startDate,
      endDate,
      requiredRole,
      location,
      requiredSkills,
      projectId,
      projects,
      allResources: resources,
      existingBookings,
      signalWeights,
    }),
    [startDate, endDate, requiredRole, location, requiredSkills, projectId, projects, resources, existingBookings, signalWeights]
  );

  // Debounced recommendation calculation — only runs when dialog is open
  useEffect(() => {
    if (!open) return;
    if (!engineInput.startDate || !engineInput.endDate) {
      setRecommendationOutput(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const timer = setTimeout(() => {
      const result = computeRecommendations(engineInput);
      setRecommendationOutput(result);
      setIsLoading(false);

      // Pre-select the first clean match only when no resource is already
      // assigned to the booking. Conflict candidates are intentionally NOT
      // pre-selected — the planner must click "Override conflict" explicitly.
      const topCleanMatch = result.cleanMatches[0];
      if (topCleanMatch && currentResourceIds.length === 0) {
        setPendingResourceIds([topCleanMatch.resource.id]);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [open, engineInput]);

  // Derived: available roles, locations, departments for filters
  const availableRoles = useMemo(
    () => [...new Set(resources.map(r => r.role).filter(Boolean))].sort(),
    [resources]
  );
  const availableLocations = useMemo(
    () => [...new Set(resources.map(r => r.location).filter((l): l is string => Boolean(l)))].sort(),
    [resources]
  );
  const availableDepartments = useMemo(
    () =>
      [...new Set(resources.map(r => r.department).filter((d): d is string => Boolean(d)))].sort(),
    [resources]
  );

  // Derived: apply filters to clean matches
  const filteredCleanMatches = useMemo(() => {
    if (!recommendationOutput) return [];
    return recommendationOutput.cleanMatches.filter(r => {
      const res = r.resource;
      const q = filter.searchQuery.toLowerCase();
      if (q) {
        const matchesQuery =
          res.name.toLowerCase().includes(q) ||
          res.role.toLowerCase().includes(q) ||
          (res.location || '').toLowerCase().includes(q) ||
          (res.skills || []).some(s => s.name.toLowerCase().includes(q));
        if (!matchesQuery) return false;
      }
      if (filter.roleFilter && res.role !== filter.roleFilter) return false;
      if (filter.locationFilter && res.location !== filter.locationFilter) return false;
      if (filter.departmentFilter && res.department !== filter.departmentFilter) return false;
      if (r.overallScore < filter.minMatchScore) return false;
      return true;
    });
  }, [recommendationOutput, filter]);

  // Derived: apply filters to conflict matches (minus dismissed)
  const filteredConflictMatches = useMemo(() => {
    if (!recommendationOutput) return [];
    return recommendationOutput.conflictMatches.filter(r => {

      const res = r.resource;
      const q = filter.searchQuery.toLowerCase();
      if (q) {
        const matchesQuery =
          res.name.toLowerCase().includes(q) ||
          res.role.toLowerCase().includes(q) ||
          (res.location || '').toLowerCase().includes(q) ||
          (res.skills || []).some(s => s.name.toLowerCase().includes(q));
        if (!matchesQuery) return false;
      }
      if (filter.roleFilter && res.role !== filter.roleFilter) return false;
      if (filter.locationFilter && res.location !== filter.locationFilter) return false;
      if (filter.departmentFilter && res.department !== filter.departmentFilter) return false;
      if (r.overallScore < filter.minMatchScore) return false;
      return true;
    });
  }, [recommendationOutput, filter]);

  const handleAdd = useCallback((result: ResourceMatchResult) => {
    // Single booking — replace the selection rather than append
    setPendingResourceIds([result.resource.id]);
  }, []);

  // Toggle for conflict candidates — replaces selection on first click, deselects on second.
  // Also dismisses the soft-gate prompt if the planner deselects while it's showing.
  const handleToggleConflict = useCallback((result: ResourceMatchResult) => {
    setPendingResourceIds(prev => {
      const isCurrentlySelected = prev.includes(result.resource.id);
      if (isCurrentlySelected) {
        setOverrideConfirmPending(false);
        return [];
      }
      // Replace whatever was previously selected — single booking, one resource only
      return [result.resource.id];
    });
  }, []);



  // True when any selected resource is a conflict candidate (not a clean match).
  // Used to colour the selection pill amber and to trigger the soft-gate.
  const conflictCandidateIds = useMemo(
    () => new Set((recommendationOutput?.conflictMatches ?? []).map(r => r.resource.id)),
    [recommendationOutput]
  );
  const hasConflictSelected = useMemo(
    () => pendingResourceIds.some(id => conflictCandidateIds.has(id)),
    [pendingResourceIds, conflictCandidateIds]
  );

  const handleConfirm = () => {
    if (hasConflictSelected && !overrideConfirmPending) {
      // First click: show the inline confirmation prompt
      setOverrideConfirmPending(true);
      return;
    }
    // Build conflict override payload — for each selected resource that is a
    // conflict candidate, include the booking they are being taken from so the
    // caller can unallocate it.
    const conflictOverrides = pendingResourceIds
      .filter(id => conflictCandidateIds.has(id))
      .flatMap(id => {
        const match = recommendationOutput?.conflictMatches.find(
          r => r.resource.id === id
        );
        if (!match?.conflictDetails) return [];
        return [{
          conflictingBookingId: match.conflictDetails.conflictingBookingId,
          resourceId: id,
        }];
      });

    // Second click (confirmed) or no conflict: commit and close
    onConfirm(pendingResourceIds, conflictOverrides);
    onOpenChange(false);
  };

  const handleCancelOverride = () => {
    setOverrideConfirmPending(false);
  };

  // The "default role" for the search bar — what was auto-applied on open
  // Once the user overrides it (clears it), we still pass the original role so
  // the bar can show the "restore default" affordance.
  const defaultRoleForBar = requiredRole ?? null;

  const hasNoDates = !startDate || !endDate;
  const hasNoResults =
    !isLoading &&
    !hasNoDates &&
    filteredCleanMatches.length === 0 &&
    filteredConflictMatches.length === 0;
  const hasActiveFilters =
    !!filter.roleFilter ||
    !!filter.locationFilter ||
    !!filter.departmentFilter ||
    filter.minMatchScore > 0 ||
    filter.searchQuery.length > 0;
  const allFilteredReason =
    hasNoResults && hasActiveFilters ? 'all-filtered' : hasNoDates ? 'no-dates' : 'no-matches';

  const showEmpty = !isLoading && (hasNoDates || hasNoResults);

  const dateRangeSummary =
    startDate && endDate
      ? `${format(startDate, 'd MMM')} – ${format(endDate, 'd MMM yyyy')}`
      : null;

  const totalSuggestions =
    (recommendationOutput?.cleanMatches.length ?? 0) +
    (recommendationOutput?.conflictMatches.length ?? 0);

  // Unfiltered total (so users can see how many are hidden by the role filter)
  const unfilteredTotal = useMemo(() => {
    if (!recommendationOutput) return 0;
    return (
      recommendationOutput.cleanMatches.length + recommendationOutput.conflictMatches.length
    );
  }, [recommendationOutput]);

  const roleFilteredTotal = filteredCleanMatches.length + filteredConflictMatches.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[740px] flex flex-col h-[85vh] max-h-[860px] p-0 gap-0 overflow-hidden"
        onInteractOutside={e => e.preventDefault()}
        onPointerDownOutside={e => e.preventDefault()}
      >
        {/* ── Header ── */}
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              {/* Icon mark */}
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 shrink-0">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold leading-tight">
                  Resource Suggestions
                </DialogTitle>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {dateRangeSummary && (
                    <div className="flex items-center gap-1">
                      <CalendarRange className="h-3.5 w-3.5 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">{dateRangeSummary}</p>
                    </div>
                  )}
                  {requiredRole && (
                    <span className="text-muted-foreground/50 text-sm">·</span>
                  )}
                  {requiredRole && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-muted-foreground">Role:</span>
                      <Badge variant="accent" accent="teal" className="text-sm px-2 py-0.5 h-6">
                        {requiredRole}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Live selection count — amber when a conflict candidate is selected */}
            {pendingResourceIds.length > 0 && (
              <Badge
                variant="accent"
                accent={hasConflictSelected ? 'amber' : 'teal'}
                className="text-sm px-2.5 py-1 shrink-0 mt-0.5 flex items-center gap-1"
              >
                {hasConflictSelected && <AlertTriangle className="h-3 w-3" />}
                {pendingResourceIds.length} selected
              </Badge>
            )}
          </div>
        </DialogHeader>

        {/* ── Divider ── */}
        <div className="border-b shrink-0" />

        {/* ── Tabs ── */}
        <Tabs
          value={activeTab}
          onValueChange={v => setActiveTab(v as 'suggested' | 'manual')}
          className="flex flex-col flex-1 min-h-0"
        >
          {/* Tab bar + search — pinned, never scrolls */}
          <div className="px-5 pt-3 pb-0 shrink-0 space-y-2.5">
            <TabsList className="h-10 w-full p-0.5">
              <TabsTrigger
                value="suggested"
                badge={!isLoading && roleFilteredTotal > 0 ? roleFilteredTotal : undefined}
                badgeVariant="secondary"
                className="flex-1 h-9 gap-2 text-sm data-[state=active]:font-medium cursor-pointer transition-all duration-150 hover:shadow-sm hover:-translate-y-px"
              >
                <Sparkles className="h-4 w-4" />
                Suggestions
              </TabsTrigger>
              <TabsTrigger
                value="manual"
                className="flex-1 h-9 gap-2 text-sm data-[state=active]:font-medium cursor-pointer transition-all duration-150 hover:shadow-sm hover:-translate-y-px"
              >
                <Search className="h-4 w-4" />
                Browse All
              </TabsTrigger>
            </TabsList>

            {activeTab === 'suggested' && (
              <RecommendationSearchBar
                filter={filter}
                onFilterChange={setFilter}
                availableRoles={availableRoles}
                availableLocations={availableLocations}
                availableDepartments={availableDepartments}
                defaultRoleFilter={defaultRoleForBar}
                onClearDefaultRole={() => setUserOverriddenDefaultRole(true)}
              />
            )}
          </div>

          {/* ── Suggestions tab ── */}
          <TabsContent
            value="suggested"
            className="flex-1 min-h-0 mt-0 data-[state=inactive]:hidden"
          >
            <ScrollArea className="h-full">
              <ScrollAreaViewport className="h-full">
                <div className="px-5 py-3 space-y-2">
                  {isLoading && <RecommendationLoadingState />}

                  {showEmpty && (
                    <EmptyRecommendationState
                      reason={allFilteredReason as 'no-dates' | 'no-matches' | 'all-filtered'}
                      onSwitchToManual={() => setActiveTab('manual')}
                    />
                  )}

                  {!isLoading && !showEmpty && startDate && endDate && (
                    <>
                      <RecommendedResourceList
                        results={filteredCleanMatches}
                        pendingResourceIds={pendingResourceIds}
                        requestedStart={startDate}
                        requestedEnd={endDate}
                        onAdd={handleAdd}
                        requiredSkills={requiredSkills}
                      />
                      <ConflictResourceList
                        results={filteredConflictMatches}
                        hasCleanMatches={filteredCleanMatches.length > 0}
                        requestedStart={startDate}
                        requestedEnd={endDate}
                        pendingResourceIds={pendingResourceIds}
                        onToggle={handleToggleConflict}
                      />
                    </>
                  )}
                </div>
              </ScrollAreaViewport>
              <ScrollAreaScrollbar orientation="vertical" />
            </ScrollArea>
          </TabsContent>

          {/* ── Browse All tab ── */}
          <TabsContent
            value="manual"
            className="flex-1 min-h-0 mt-0 data-[state=inactive]:hidden"
          >
            <div className="flex flex-col h-full">
              {/* Inline compact list */}
              <ScrollArea className="flex-1 min-h-0">
                <ScrollAreaViewport className="h-full">
                  <div className="px-4 py-3">
                    <MultiResourceSelect
                      resources={resources}
                      selectedResourceIds={pendingResourceIds}
                      onSelect={setPendingResourceIds}
                    />
                  </div>
                </ScrollAreaViewport>
                <ScrollAreaScrollbar orientation="vertical" />
              </ScrollArea>

              {/* Open full lookup modal */}
              <div className="border-t px-4 py-2.5 shrink-0 bg-muted/20">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full text-xs gap-2"
                  onClick={() => setResourceSearchOpen(true)}
                >
                  <Search className="h-3.5 w-3.5" />
                  Open full resource lookup
                </Button>
              </div>
            </div>

            {/* Full-screen resource search modal */}
            <ResourceSearchModal
              open={resourceSearchOpen}
              onOpenChange={setResourceSearchOpen}
              resources={resources}
              selectedResourceIds={pendingResourceIds}
              onConfirm={setPendingResourceIds}
            />
          </TabsContent>
        </Tabs>

        {/* ── Footer ── */}
        <div className="border-t shrink-0 bg-muted/30">
          {/* Soft-gate: inline conflict acknowledgement prompt */}
          {overrideConfirmPending && (
            <div className="px-6 pt-4 pb-0">
              <div className="flex items-start gap-2.5 rounded-lg border border-amber-300/60 bg-amber-50/80 dark:bg-amber-950/30 dark:border-amber-700/40 px-3.5 py-3">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                    Confirm conflict override?
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5 leading-snug">
                    The selected resource has an existing booking conflict. The project they are currently assigned to may be left without resource coverage. This override will be logged against the booking.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="px-6 py-4 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {pendingResourceIds.length === 0 ? (
                'No resources selected'
              ) : (
                <>
                  <span className="font-medium text-foreground">{pendingResourceIds.length}</span>
                  {' '}resource{pendingResourceIds.length !== 1 ? 's' : ''} selected
                </>
              )}
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="transition-all duration-150 hover:shadow-sm hover:-translate-y-px active:translate-y-0"
                onClick={overrideConfirmPending ? handleCancelOverride : () => onOpenChange(false)}
              >
                {overrideConfirmPending ? 'Go back' : 'Cancel'}
              </Button>
              <Button
                type="button"
                variant={overrideConfirmPending ? 'destructive' : 'default'}
                disabled={pendingResourceIds.length === 0}
                className="gap-1.5 transition-all duration-150 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0"
                onClick={handleConfirm}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                {overrideConfirmPending ? 'Confirm override' : 'Confirm assignment'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
